/**
 * Admin Save All advisory for field-box quality (ink over print, duplicates, etc.).
 * Advisory only — never blocks or rolls back a successful save.
 */

const MAX_LISTED = 12;

const REASON_COPY = {
  'over-print': 'box covers printed content',
  'printed-content-inside': 'may include printed label',
  duplicate: 'overlaps another field',
  degenerate: 'invalid or empty box',
};

export function humanizeFieldQualityReason(reason, verdict) {
  const key = String(reason || '').toLowerCase();
  if (REASON_COPY[key]) return REASON_COPY[key];
  if (String(verdict || '').toLowerCase() === 'drop') return 'box looks unreliable';
  if (String(verdict || '').toLowerCase() === 'review') return 'may need a closer look';
  return reason || 'needs review';
}

/**
 * @param {object} report FieldQualityReport from GET .../field-quality
 * @param {{ maxList?: number, labelByName?: Map<string,string>|Record<string,string> }} [opts]
 * @returns {{ issueCount: number, message: string } | null} null when nothing to show
 */
export function buildFieldQualityAdvisory(report, opts = {}) {
  if (!report) return null;
  const review = Number(report.review) || 0;
  const drop = Number(report.drop) || 0;
  const issueCount = review + drop;
  if (issueCount <= 0) return null;

  const maxList = opts.maxList ?? MAX_LISTED;
  const labelByName = opts.labelByName || {};
  const lookup = (name) => {
    if (!name) return name;
    if (typeof labelByName.get === 'function') return labelByName.get(name) || name;
    return labelByName[name] || name;
  };

  const problems = (report.fields || []).filter((f) => {
    const v = String(f.verdict || '').toLowerCase();
    return v === 'review' || v === 'drop';
  });

  // Prefer drops first so the worst boxes surface in the capped list.
  problems.sort((a, b) => {
    const rank = (v) => (String(v).toLowerCase() === 'drop' ? 0 : 1);
    return rank(a.verdict) - rank(b.verdict);
  });

  const listed = problems.slice(0, maxList);
  const lines = listed.map((f) => {
    const name = lookup(f.pdfFieldName) || f.pdfFieldName || f.fieldId || '(unnamed)';
    const page = f.page != null ? `p.${f.page}` : '?';
    const verdict = String(f.verdict || '').toLowerCase();
    const why = humanizeFieldQualityReason(f.reason, verdict);
    return `• ${name} (${page}, ${verdict}): ${why}`;
  });

  const remaining = problems.length - listed.length;
  if (remaining > 0) {
    lines.push(`… and ${remaining} more`);
  }

  const summary =
    `Saved. ${issueCount} field box${issueCount === 1 ? '' : 'es'} may need review` +
    (drop > 0 ? ` (${drop} drop, ${review} review)` : review > 0 ? ` (${review} review)` : '') +
    '.\n\n' +
    'These boxes may sit on printed content, logos, or overlap another field. ' +
    'You can keep the save and fix them when convenient.\n\n' +
    lines.join('\n');

  return { issueCount, message: summary };
}

export default { buildFieldQualityAdvisory, humanizeFieldQualityReason };
